import { useEffect, useMemo, useRef, useState } from 'react';

import { getVisibleQuestionsForQuotaGroup } from '@api/instant_survey';
import type { Group, Question, QuestionLogic } from '@api/instant_survey/types';

import { type Item, type PreviewSelection } from './helpers';
import { useStatsigClient } from '@statsig/react-bindings';
import { GATES } from '@constants';

interface UseSurveyPreviewParams {
  questionLogic?: Record<string, QuestionLogic>;
  questions?: Question[];
  quotaGroups?: Group[];
}

/**
 * Checks whether the current user can preview surveys.
 */
export const useCanPreviewSurveys = () => {
  const { client } = useStatsigClient();
  return client.checkGate(GATES.PREVIEW_SURVEY);
};

/**
 * Consolidates the state and handlers needed to render survey previews.
 * The selected quota group controls which questions are available in the
 * preview dropdown, and changing quota groups resets the preview to that
 * group's first visible question.
 */
export const useSurveyPreview = ({
  questionLogic,
  questions,
  quotaGroups,
}: UseSurveyPreviewParams) => {
  const initialPreviewSelection = getFirstPreviewSelection({
    questionLogic,
    questions,
    quotaGroups,
  });
  const { previewSelection, resetPreviewSelection, setPreviewSelection } = usePreviewSelection({
    questionId: initialPreviewSelection.questionId,
    quotaGroupId: initialPreviewSelection.quotaGroupId,
  });

  const previewQuestions = useMemo(
    () =>
      getVisibleQuestionsForQuotaGroup({
        questionLogic,
        questions,
        quotaGroupId: previewSelection.quotaGroupId,
      }),
    [questionLogic, previewSelection.quotaGroupId, questions]
  );

  const handleUpdatePreviewQuotaGroup = (quotaGroupId: number) => {
    const nextQuestions = getVisibleQuestionsForQuotaGroup({
      questionLogic,
      questions,
      quotaGroupId,
    });

    setPreviewSelection((prev) => {
      return {
        ...prev,
        questionId: nextQuestions[0]?.id,
        quotaGroupId,
      };
    });
  };

  const handleUpdatePreviewQuestion = (questionId: number) => {
    setPreviewSelection((prev) => {
      return {
        ...prev,
        ...getPreviewSelectionForQuestion({
          currentQuotaGroupId: prev.quotaGroupId,
          questionId,
          questionLogic,
          questions,
          quotaGroups,
        }),
      };
    });
  };

  const resetPreviewQuestion = () => {
    const nextQuestionId = previewQuestions[0]?.id;
    if (nextQuestionId === undefined) return;

    setPreviewSelection((prev) => {
      return {
        ...prev,
        questionId: nextQuestionId,
        restartKey: (prev.restartKey ?? 0) + 1,
      };
    });
  };

  const [showPreviewModal, handleClosePreviewModal, handlePreviewButtonClick] = useShowPreviewModal(
    {
      close: resetPreviewSelection,
      updateQuestion: handleUpdatePreviewQuestion,
    }
  );
  useValidPreviewSelection({
    questionLogic,
    questions,
    quotaGroups,
    setPreviewSelection,
  });
  return {
    handleClosePreviewModal,
    handlePreviewButtonClick,
    handleUpdatePreviewQuestion,
    handleUpdatePreviewQuotaGroup,
    previewQuestions,
    previewSelection,
    resetPreviewQuestion,
    showPreviewModal,
  };
};

/**
 * Owns the active preview selection and keeps the latest valid initial selection
 * available for modal close/reset.
 */
const usePreviewSelection = ({ questionId, quotaGroupId }: PreviewSelection) => {
  const latestInitialPreviewSelectionRef = useRef<PreviewSelection>({
    questionId,
    quotaGroupId,
    restartKey: 0,
  });
  const [previewSelection, setPreviewSelection] = useState<PreviewSelection>(
    latestInitialPreviewSelectionRef.current
  );

  useEffect(() => {
    latestInitialPreviewSelectionRef.current = {
      questionId,
      quotaGroupId,
      restartKey: 0,
    };
  }, [questionId, quotaGroupId]);

  const resetPreviewSelection = () => setPreviewSelection(latestInitialPreviewSelectionRef.current);

  return {
    previewSelection,
    resetPreviewSelection,
    setPreviewSelection,
  };
};

/**
 * Controls preview modal visibility. Opening from a question action can update the
 * selected question before showing the modal; closing also resets selection state.
 */
const useShowPreviewModal = ({
  close,
  updateQuestion,
}: {
  close: () => void;
  updateQuestion: (questionId: number) => void;
}) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    close();
  };

  const handlePreviewButtonClick = (questionId?: number) => {
    if (typeof questionId === 'number') updateQuestion(questionId);
    setShowPreviewModal(true);
  };

  return [showPreviewModal, handleClosePreviewModal, handlePreviewButtonClick] as const;
};

/**
 * Repairs stale preview selections after question, logic, or quota group changes.
 * Quota group validity is resolved first, then the question is validated against
 * the questions visible for that quota group.
 */
const useValidPreviewSelection = ({
  questionLogic,
  questions,
  quotaGroups,
  setPreviewSelection,
}: UseSurveyPreviewParams & {
  setPreviewSelection: React.Dispatch<React.SetStateAction<PreviewSelection>>;
}) => {
  useEffect(() => {
    if (!questions || !quotaGroups) return;

    setPreviewSelection((prev) => {
      const getVisibleQuestions = (quotaGroupId?: number) =>
        getVisibleQuestionsForQuotaGroup({
          questionLogic,
          questions,
          quotaGroupId,
        });

      if (!prev) return getFirstPreviewSelection({ questionLogic, questions, quotaGroups });

      const { questionId, quotaGroupId } = prev;
      const quotaGroupIsValid = checkIfValid(quotaGroups, quotaGroupId);
      const nextQuestions = quotaGroupIsValid ? getVisibleQuestions(quotaGroupId) : [];
      const questionIsValid = checkIfValid(nextQuestions, questionId);

      if (questionIsValid && quotaGroupIsValid) return prev;

      if (quotaGroupIsValid && nextQuestions.length) {
        return {
          ...prev,
          questionId: nextQuestions[0]?.id,
          quotaGroupId,
        };
      }

      return {
        ...prev,
        ...getFirstPreviewSelection({ questionLogic, questions, quotaGroups }),
      };
    });
  }, [questionLogic, questions, quotaGroups, setPreviewSelection]);
};

const checkIfValid = (items: Item[], id?: number) => {
  if (id === undefined || id === null) return false;
  return items.some((item) => String(item.id) === String(id));
};

const getFirstPreviewSelection = ({
  questionLogic,
  questions,
  quotaGroups,
}: UseSurveyPreviewParams): Pick<PreviewSelection, 'questionId' | 'quotaGroupId'> => {
  const getVisibleQuestions = (quotaGroupId?: number) =>
    getVisibleQuestionsForQuotaGroup({
      questionLogic,
      questions,
      quotaGroupId,
    });
  const quotaGroup = quotaGroups?.find((group) => getVisibleQuestions(group.id).length);
  const quotaGroupId = quotaGroup?.id ?? quotaGroups?.[0]?.id;

  return {
    questionId: getVisibleQuestions(quotaGroupId)[0]?.id,
    quotaGroupId,
  };
};

/**
 * Preserve a direct question preview when possible. If the requested question is hidden for the
 * current quota group, select the first quota group where that question is visible.
 */
const getPreviewSelectionForQuestion = ({
  currentQuotaGroupId,
  questionId,
  questionLogic,
  questions,
  quotaGroups,
}: UseSurveyPreviewParams & {
  currentQuotaGroupId?: number;
  questionId: number;
}): Pick<PreviewSelection, 'questionId' | 'quotaGroupId'> => {
  const currentQuotaGroupQuestions = getVisibleQuestionsForQuotaGroup({
    questionLogic,
    questions,
    quotaGroupId: currentQuotaGroupId,
  });
  const questionIsVisibleForCurrentQuotaGroup = checkIfValid(
    currentQuotaGroupQuestions,
    questionId
  );

  if (questionIsVisibleForCurrentQuotaGroup) {
    return {
      questionId,
      quotaGroupId: currentQuotaGroupId,
    };
  }

  const firstVisibleQuotaGroup = quotaGroups?.find((quotaGroup) =>
    getVisibleQuestionsForQuotaGroup({
      questionLogic,
      questions,
      quotaGroupId: quotaGroup.id,
    }).some((question) => String(question.id) === String(questionId))
  );

  return {
    questionId: firstVisibleQuotaGroup ? questionId : currentQuotaGroupQuestions[0]?.id,
    quotaGroupId: firstVisibleQuotaGroup?.id ?? currentQuotaGroupId,
  };
};
